import { Order, Product } from '@typings';

import { isDefined } from '../is';

import { GA4EventParams, GAItem } from './gtm';

type GAItemCategoryFields = {
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
};

const MAX_CATEGORIES = 5;

const categoryKeys = ['item_category', 'item_category2', 'item_category3', 'item_category4', 'item_category5'] as const;

export const mapCategoriesToGAItem = (categories?: string | string[] | null): GAItemCategoryFields => {
  if (!isDefined(categories)) {
    return {};
  }

  if (typeof categories === 'string') {
    return { item_category: categories };
  }

  return categories.slice(0, MAX_CATEGORIES).reduce((acc, category, index) => {
    const key = categoryKeys[index] as keyof GAItemCategoryFields;

    return { ...acc, [key]: category };
  }, {});
};

export const mapOrderProductsToGAItems = (
  orderDetails: Order.Single,
  selectOnlyProductsWithQuantity: boolean = false,
  {
    item_list_id,
    item_list_name,
  }: {
    item_list_id?: string;
    item_list_name?: string;
  } = {},
): GAItem[] => {
  return orderDetails.order.products
    .map(({ product, priceEachAsNumber, items }, index) => {
      const { products } = orderDetails;
      if (Array.isArray(products)) {
        return null;
      }
      const productInfo = products[product];
      if (!isDefined(productInfo)) {
        return null;
      }
      const quantity = items.reduce((acc, cur) => acc + cur.quantity, 0);
      if (selectOnlyProductsWithQuantity && quantity === 0) {
        return null;
      }

      return mapProductToGAItem(productInfo, { index, item_list_id, item_list_name, orderPrice: priceEachAsNumber, quantity });
    })
    .filter(isDefined);
};

export const mapProductToGAItem = (
  productInfo: Product.Full | Product.Standard,
  {
    quantity,
    index,
    globalIndexOffset = 0,
    item_list_id,
    item_list_name,
    orderPrice,
  }: {
    index?: number;
    quantity?: number;
    orderPrice?: number;
    globalIndexOffset?: number;
    item_list_id?: string;
    item_list_name?: string;
  } = {},
): GAItem => {
  return {
    discount: productInfo.discountPercent,
    index: isDefined(index) && isDefined(globalIndexOffset) ? index + globalIndexOffset : undefined,
    item_brand: productInfo.brandName ?? undefined,
    item_id: productInfo.sku,
    item_list_id,
    item_list_name,
    item_name: productInfo.name,
    item_variant: productInfo.variant,
    price: orderPrice ?? productInfo.priceAsNumber,
    quantity,
    ...mapCategoriesToGAItem(productInfo.categoryName),
  };
};

export const mapProductsToGAItems = (
  products: (Product.Full | Product.Standard)[],
  options: {
    quantity?: number;
    globalIndexOffset?: number;
    item_list_id?: string;
    item_list_name?: string;
  } = {},
): GAItem[] => {
  return products.map((productInfo, index) => mapProductToGAItem(productInfo, { ...options, index })).filter(isDefined);
};

export const buildTransactionData = (orders: Order.Essentials[] | Order.Closed[], orderData: Order.Single) => {
  const { products } = orderData;
  if (Array.isArray(products)) {
    return;
  }

  return orders.map(order => {
    const ga4Items = orderData.order.products
      .map(({ product, priceEachAsNumber, items }, index) => {
        const productInfo = products[product];
        if (!isDefined(productInfo)) {
          return null;
        }
        const quantity = items.reduce((acc, cur) => acc + cur.quantity, 0);

        return mapProductToGAItem(productInfo, { index, orderPrice: priceEachAsNumber, quantity });
      })
      .filter(isDefined);

    const ga4EcommerceData: GA4EventParams['purchase'] = {
      ecommerce: {
        currency: order.currency.currency,
        items: ga4Items,
        shipping: order.totals.shippingPriceAsNumber,
        tax: order.totals.grandTotalPriceTaxAsNumber,
        transaction_id: order.order,
        value: order.totals.itemsTotalPriceAsNumber,
      },
    };

    return {
      event: 'purchase',
      ...ga4EcommerceData,
    };
  });
};
